import { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Users, Store, Package, Trash2, Eye, UserCheck } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/superbackend`;
const getH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('sellmu_super_token')}` } });

export default function SuperUsersPage() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [uRes, sRes] = await Promise.all([
        axios.get(`${API}/users`, getH()),
        axios.get(`${API}/stats`, getH())
      ]);
      setUsers(uRes.data);
      setStats(sRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/users/${deleteId}`, getH());
      toast.success('User dan semua data terkait dihapus');
      setDeleteId(null);
      loadData();
    } catch (err) { toast.error('Gagal menghapus'); }
  };

  const statCards = [
    { label: 'Total Admins', value: stats?.total_admins || 0, icon: Users, color: 'text-[#EB7B44]', bg: 'bg-[#EB7B44]/10' },
    { label: 'Total Employees', value: stats?.total_employees || 0, icon: UserCheck, color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { label: 'Total Stores', value: stats?.total_stores || 0, icon: Store, color: 'text-green-500', bg: 'bg-green-500/10' },
    { label: 'Total Packages', value: stats?.total_packages || 0, icon: Package, color: 'text-purple-500', bg: 'bg-purple-500/10' },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="super-users-page">
      <h1 className="text-2xl font-bold text-[#202020]" style={{ fontFamily: 'Manrope' }}>User Management</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
        {statCards.map(c => (
          <Card key={c.label} className="bg-white border-[#CACACC] rounded-xl shadow-sm animate-fade-in">
            <CardContent className="p-5 flex items-center gap-4">
              <div className={`w-11 h-11 rounded-xl ${c.bg} flex items-center justify-center`}>
                <c.icon size={20} className={c.color} strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-xs font-medium text-[#64584F] uppercase tracking-wider">{c.label}</p>
                <p className="text-xl font-bold text-[#202020]" style={{ fontFamily: 'Manrope' }}>{c.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-white border-[#CACACC] rounded-xl shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F6F5F5] border-b border-[#CACACC]">
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider">Name</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider hidden sm:table-cell">Email</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider hidden md:table-cell">Stores</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider hidden md:table-cell">Products</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider hidden lg:table-cell">Employees</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider hidden lg:table-cell">Registered</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-[#A6A2A0]">Loading...</TableCell></TableRow>
            ) : users.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-[#A6A2A0]">No users yet</TableCell></TableRow>
            ) : users.map(u => (
              <TableRow key={u.id} className="border-b border-[#DFDEDE] hover:bg-[#F6F5F5]/50 transition-colors" data-testid={`super-user-row-${u.id}`}>
                <TableCell className="font-medium text-[#202020]">{u.name}</TableCell>
                <TableCell className="text-[#64584F] hidden sm:table-cell">{u.email}</TableCell>
                <TableCell className="hidden md:table-cell"><Badge className="bg-green-100 text-green-700 border border-green-200 text-xs">{u.store_count}</Badge></TableCell>
                <TableCell className="hidden md:table-cell"><Badge className="bg-blue-100 text-blue-700 border border-blue-200 text-xs">{u.product_count}</Badge></TableCell>
                <TableCell className="hidden lg:table-cell"><Badge className="bg-purple-100 text-purple-700 border border-purple-200 text-xs">{u.employee_count}</Badge></TableCell>
                <TableCell className="text-[#64584F] text-sm hidden lg:table-cell">{u.created_at?.split('T')[0]}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setDetail(u)}><Eye size={15} className="text-[#64584F]" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteId(u.id)} data-testid={`super-delete-user-${u.id}`}><Trash2 size={15} className="text-red-500" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="bg-white border-[#CACACC] rounded-xl max-w-md">
          <DialogHeader><DialogTitle className="text-[#202020]" style={{ fontFamily: 'Manrope' }}>User Detail</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-[#64584F]">Name</span><span className="font-medium text-[#202020]">{detail.name}</span></div>
              <div className="flex justify-between"><span className="text-[#64584F]">Email</span><span className="font-medium text-[#202020]">{detail.email}</span></div>
              <div className="flex justify-between"><span className="text-[#64584F]">Stores</span><span className="font-medium text-[#202020]">{detail.store_count}</span></div>
              <div className="flex justify-between"><span className="text-[#64584F]">Products</span><span className="font-medium text-[#202020]">{detail.product_count}</span></div>
              <div className="flex justify-between"><span className="text-[#64584F]">Employees</span><span className="font-medium text-[#202020]">{detail.employee_count}</span></div>
              <div className="flex justify-between"><span className="text-[#64584F]">Registered</span><span className="font-medium text-[#202020]">{detail.created_at?.split('T')[0]}</span></div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="bg-white border-[#CACACC] rounded-xl max-w-sm">
          <DialogHeader><DialogTitle className="text-[#202020]">Hapus user ini? Semua data terkait akan dihapus permanen.</DialogTitle></DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteId(null)} className="border-[#CACACC]">Batal</Button>
            <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="super-confirm-delete-user">Hapus</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
